import React from 'react';
import apiClient from '../../shared/services/apiService.js';

const StatsOverview = () => {
  const [stats, setStats] = React.useState({ posts: 0, users: 0, locations: 0 });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  const items = React.useMemo(() => ([
    { value: stats.posts, label: "Appels d'offres", icon: '📣' },
    { value: stats.users, label: 'Utilisateurs', icon: '👥' },
    { value: stats.locations, label: 'Localités', icon: '🗺️' },
  ]), [stats]);

  React.useEffect(() => {
    let mounted = true;
    let abortController = new AbortController();
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('[StatsOverview] Calling API...');
        
        const res = await apiClient.get('/homeSections/getStats', {
          params: { fetchData: 1 },
          headers: { 'X-Skip-Auth': true },
          signal: abortController.signal
        });
        
        console.log('[StatsOverview] API Response:', res?.data);
        
        const root = res?.data;
        const counts = root?.result?.data?.count || root?.data?.count || root?.count || {};
        
        console.log('[StatsOverview] Parsed counts:', counts);
        
        if (!mounted) return;
        setStats({
          posts: Number(counts.posts) || 0,
          users: Number(counts.users) || 0,
          locations: Number(counts.locations) || 0,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('[StatsOverview] Request was aborted');
          return;
        }
        console.error('[StatsOverview] API Error:', err);
        if (!mounted) return;
        setError(err.message || 'Erreur lors du chargement des statistiques');
        // Keep default values on error
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    
    return () => { 
      mounted = false; 
      abortController.abort();
    };
  }, []);

  return (
    <section className="modern-stats-overview">
      <div className="container">
        <div className="stats-container">
          {error && (
            <div className="modern-alert">
              <i className="fa-solid fa-exclamation-triangle"></i>
              <div>
                <strong>Information:</strong> {error}. Affichage des valeurs par défaut.
              </div>
            </div>
          )}
          
          {loading && (
            <div className="modern-loading">
              <div className="loading-spinner"></div>
              <p>Chargement des statistiques...</p>
            </div>
          )}
          
          <div className="stats-grid">
            {items.map((it, idx) => (
              <div key={idx} className="stat-card">
                <div className="stat-card-inner">
                  <div className="stat-icon">
                    <span className="icon-emoji">{it.icon}</span>
                    <div className="icon-glow"></div>
                  </div>
                  
                  <div className="stat-content">
                    <div className="stat-number">
                      {loading ? (
                        <div className="number-loading">
                          <span>.</span><span>.</span><span>.</span>
                        </div>
                      ) : (
                        <span className="number-value">{it.value.toLocaleString()}</span>
                      )}
                      {!loading && !error && stats.posts > 0 && (
                        <div className="stat-status">
                          <i className="fa-solid fa-check-circle"></i>
                        </div>
                      )}
                    </div>
                    <div className="stat-label">{it.label}</div>
                  </div>
                  
                  <div className="stat-decoration"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .modern-stats-overview {
          padding: 60px 0;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          position: relative;
          overflow: hidden;
        }

        .modern-stats-overview::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>') repeat;
          opacity: 0.3;
        }

        .stats-container {
          position: relative;
          z-index: 1;
        }

        .modern-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(245, 158, 11, 0.15);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 40px;
          color: #fbbf24;
          backdrop-filter: blur(10px);
        }

        .modern-loading {
          text-align: center;
          padding: 60px 20px;
          color: #64748b;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #10b981;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
          max-width: 900px;
          margin: 0 auto;
        }

        .stat-card {
          position: relative;
        }

        .stat-card-inner {
          background: white;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 24px;
          padding: 40px 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .stat-card:hover .stat-card-inner {
          transform: translateY(-8px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          background: white;
          border-color: rgba(16, 185, 129, 0.3);
        }

        .stat-icon {
          position: relative;
          margin-bottom: 24px;
          display: inline-block;
        }

        .icon-emoji {
          font-size: 3rem;
          display: block;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
          transition: all 0.3s ease;
        }

        .stat-card:hover .icon-emoji {
          transform: scale(1.1) rotate(5deg);
        }

        .icon-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .stat-card:hover .icon-glow {
          opacity: 1;
        }

        .stat-content {
          position: relative;
          z-index: 2;
        }

        .stat-number {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .number-value {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e293b;
          text-shadow: none;
          line-height: 1;
        }

        .number-loading {
          display: flex;
          gap: 4px;
        }

        .number-loading span {
          font-size: 2.5rem;
          font-weight: 800;
          color: #64748b;
          animation: loading-dots 1.4s ease-in-out infinite both;
        }

        .number-loading span:nth-child(1) { animation-delay: -0.32s; }
        .number-loading span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes loading-dots {
          0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          40% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .stat-status {
          color: #10b981;
          font-size: 1.2rem;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }

        .stat-label {
          font-size: 1rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-shadow: none;
        }

        .stat-decoration {
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent 30%, rgba(16, 185, 129, 0.05) 50%, transparent 70%);
          transform: rotate(-45deg);
          transition: all 0.6s ease;
          opacity: 0;
        }

        .stat-card:hover .stat-decoration {
          opacity: 1;
          transform: rotate(-45deg) translate(50%, -50%);
        }

        @media (max-width: 768px) {
          .modern-stats-overview {
            padding: 40px 0;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 20px;
            padding: 0 20px;
          }

          .stat-card-inner {
            padding: 30px 20px;
          }

          .number-value {
            font-size: 2rem;
          }

          .icon-emoji {
            font-size: 2.5rem;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stat-card-inner {
            padding: 24px 16px;
          }
        }
      `}</style>
    </section>
  );
};

export default StatsOverview;
